import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Users, MessageCircle, Plus, ArrowLeft, Send, 
  Loader2, Crown, Clock, User, Trash2, CheckSquare, Square, Smile, Image, SmilePlus, Mic, Film, FileText, X
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import MentionTextarea from '../components/MentionTextarea';
import EmojiPicker from 'emoji-picker-react';
import GifPicker from '../components/GifPicker';
import VoiceRecorder from '../components/VoiceRecorder';
import FileAttachment from '../components/FileAttachment';
import MediaRenderer from '../components/MediaRenderer';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Admin emails for delete permissions
const ADMIN_EMAILS = ['info@heatherstewart.coach'];

// Community Page Component (Free, Founding, or VIP)
export const CommunityPage = ({ communityType = 'free', title = 'Community', badgeText = '', badgeColor = '' }) => {
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newThreadOpen, setNewThreadOpen] = useState(false);
  const [newThreadTitle, setNewThreadTitle] = useState('');
  const [newThreadContent, setNewThreadContent] = useState('');
  const [newPostContent, setNewPostContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectedThreadIds, setSelectedThreadIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [showThreadEmojiPicker, setShowThreadEmojiPicker] = useState(false);
  const [showPostEmojiPicker, setShowPostEmojiPicker] = useState(false);
  const [showThreadGifPicker, setShowThreadGifPicker] = useState(false);
  const [showPostGifPicker, setShowPostGifPicker] = useState(false);
  const [showReactionPicker, setShowReactionPicker] = useState(null); // post id or 'thread'
  const [threadMediaFile, setThreadMediaFile] = useState(null);
  const [postMediaFile, setPostMediaFile] = useState(null);
  const [showThreadVoiceRecorder, setShowThreadVoiceRecorder] = useState(false);
  const [showPostVoiceRecorder, setShowPostVoiceRecorder] = useState(false);
  const { getAuthHeader, isFoundingMember, isVip, user } = useAuth();
  const navigate = useNavigate();
  const threadEmojiRef = useRef(null);
  const postEmojiRef = useRef(null);
  const threadGifRef = useRef(null);
  const postGifRef = useRef(null);
  const reactionPickerRef = useRef(null);
  const threadTextareaRef = useRef(null);
  const postTextareaRef = useRef(null);

  const isPremiumCommunity = communityType !== 'free';

  useEffect(() => {
    // Check access
    if (communityType === 'founding' && !isFoundingMember && !isVip) {
      navigate('/pricing');
      toast.error('Founding Member access required');
      return;
    }
    if (communityType === 'vip' && !isVip) {
      navigate('/pricing');
      toast.error('VIP Member access required');
      return;
    }
    fetchThreads();
  }, [communityType, isFoundingMember, isVip]);

  // Close emoji/gif picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (threadEmojiRef.current && !threadEmojiRef.current.contains(event.target)) {
        setShowThreadEmojiPicker(false);
      }
      if (postEmojiRef.current && !postEmojiRef.current.contains(event.target)) {
        setShowPostEmojiPicker(false);
      }
      if (threadGifRef.current && !threadGifRef.current.contains(event.target)) {
        setShowThreadGifPicker(false);
      }
      if (postGifRef.current && !postGifRef.current.contains(event.target)) {
        setShowPostGifPicker(false);
      }
      if (reactionPickerRef.current && !reactionPickerRef.current.contains(event.target)) {
        setShowReactionPicker(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const insertAtCursor = (ref, value, setter, text) => {
    const el = ref.current;
    if (el) {
      const start = el.selectionStart ?? value.length;
      const end = el.selectionEnd ?? value.length;
      const newValue = value.slice(0, start) + text + value.slice(end);
      setter(newValue);
      // Restore cursor after the inserted text
      setTimeout(() => {
        el.focus();
        const newPos = start + text.length;
        el.setSelectionRange(newPos, newPos);
      }, 0);
    } else {
      setter(prev => prev + text);
    }
  };

  const onThreadEmojiClick = (emojiData) => {
    insertAtCursor(threadTextareaRef, newThreadContent, setNewThreadContent, emojiData.emoji);
  };

  const onPostEmojiClick = (emojiData) => {
    insertAtCursor(postTextareaRef, newPostContent, setNewPostContent, emojiData.emoji);
  };

  const onThreadGifSelect = (gifData) => {
    insertAtCursor(threadTextareaRef, newThreadContent, setNewThreadContent, `\n[GIF](${gifData.url})\n`);
    setShowThreadGifPicker(false);
  };

  const onPostGifSelect = (gifData) => {
    insertAtCursor(postTextareaRef, newPostContent, setNewPostContent, `\n[GIF](${gifData.url})\n`);
    setShowPostGifPicker(false);
  };

  const handleAddReaction = async (targetId, emoji, isThread = false) => {
    try {
      const endpoint = isThread 
        ? `${API_URL}/community/threads/${targetId}/reactions`
        : `${API_URL}/community/posts/${targetId}/reactions`;
      
      const response = await axios.post(endpoint, { emoji }, {
        headers: getAuthHeader()
      });
      
      // Update local state
      if (isThread && selectedThread) {
        setSelectedThread({
          ...selectedThread,
          reactions: response.data.reactions
        });
      } else {
        setPosts(posts.map(p => 
          p.id === targetId 
            ? { ...p, reactions: response.data.reactions }
            : p
        ));
      }
      
      setShowReactionPicker(null);
    } catch (error) {
      toast.error('Failed to add reaction');
    }
  };

  // Common reactions for quick access
  const quickReactions = ['❤️', '👍', '😂', '😮', '😢', '🎉'];

  const fetchThreads = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/community/threads?community_type=${communityType}`,
        { headers: getAuthHeader() }
      );
      setThreads(response.data);
    } catch (error) {
      console.error('Failed to fetch threads:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPosts = async (threadId) => {
    try {
      const response = await axios.get(
        `${API_URL}/community/threads/${threadId}/posts`,
        { headers: getAuthHeader() }
      );
      setPosts(response.data);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    }
  };

  const handleSelectThread = async (thread) => {
    setSelectedThread(thread);
    await fetchPosts(thread.id);
  };

  const isAdmin = user?.email && ADMIN_EMAILS.includes(user.email);

  const handleDeleteThread = async (threadId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this thread and all its replies? This cannot be undone.')) {
      return;
    }
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/admin/community/threads/${threadId}`, {
        headers: getAuthHeader()
      });
      setThreads(threads.filter(t => t.id !== threadId));
      if (selectedThread?.id === threadId) {
        setSelectedThread(null);
        setPosts([]);
      }
      toast.success('Thread deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete thread');
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleSelect = (threadId, e) => {
    e.stopPropagation();
    setSelectedThreadIds(prev => 
      prev.includes(threadId) 
        ? prev.filter(id => id !== threadId)
        : [...prev, threadId]
    );
  };

  const handleSelectAll = () => {
    if (selectedThreadIds.length === threads.length) {
      setSelectedThreadIds([]);
    } else {
      setSelectedThreadIds(threads.map(t => t.id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedThreadIds.length === 0) {
      toast.error('No threads selected');
      return;
    }
    if (!window.confirm(`Are you sure you want to delete ${selectedThreadIds.length} thread(s) and all their replies? This cannot be undone.`)) {
      return;
    }
    setBulkDeleting(true);
    try {
      const response = await axios.post(
        `${API_URL}/admin/community/threads/bulk-delete`,
        { thread_ids: selectedThreadIds },
        { headers: getAuthHeader() }
      );
      setThreads(threads.filter(t => !selectedThreadIds.includes(t.id)));
      if (selectedThread && selectedThreadIds.includes(selectedThread.id)) {
        setSelectedThread(null);
        setPosts([]);
      }
      setSelectedThreadIds([]);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete threads');
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleCreateThread = async () => {
    if (!newThreadTitle.trim() && !threadMediaFile) {
      toast.error('Please add a title');
      return;
    }
    if (!newThreadContent.trim() && !threadMediaFile) {
      toast.error('Please add content or attach a file');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        title: newThreadTitle,
        content: newThreadContent || (threadMediaFile ? threadMediaFile.file_name : ''),
        community_type: communityType
      };
      if (threadMediaFile) {
        let msgType = threadMediaFile.file_type;
        if (msgType === 'document') msgType = 'file';
        payload.message_type = msgType;
        payload.file_url = threadMediaFile.file_url;
        payload.file_name = threadMediaFile.file_name;
        payload.file_size = threadMediaFile.file_size;
      }
      const response = await axios.post(
        `${API_URL}/community/threads`,
        payload,
        { headers: getAuthHeader() }
      );
      setThreads([response.data, ...threads]);
      setNewThreadTitle('');
      setNewThreadContent('');
      setThreadMediaFile(null);
      setNewThreadOpen(false);
      toast.success('Thread created!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create thread');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreatePost = async () => {
    if (!newPostContent.trim() && !postMediaFile) return;
    if (!selectedThread) return;
    setSubmitting(true);
    try {
      const payload = {
        thread_id: selectedThread.id,
        content: newPostContent || (postMediaFile ? postMediaFile.file_name : '')
      };
      if (postMediaFile) {
        let msgType = postMediaFile.file_type;
        if (msgType === 'document') msgType = 'file';
        payload.message_type = msgType;
        payload.file_url = postMediaFile.file_url;
        payload.file_name = postMediaFile.file_name;
        payload.file_size = postMediaFile.file_size;
      }
      const response = await axios.post(
        `${API_URL}/community/posts`,
        payload,
        { headers: getAuthHeader() }
      );
      setPosts([...posts, response.data]);
      setNewPostContent('');
      setPostMediaFile(null);
      // Update thread post count
      setThreads(threads.map(t => 
        t.id === selectedThread.id 
          ? { ...t, post_count: t.post_count + 1 }
          : t
      ));
      toast.success('Reply posted!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post reply');
    } finally {
      setSubmitting(false);
    }
  };

  const handleThreadVoiceSend = async (audioBlob, duration) => {
    const formData = new FormData();
    const ext = audioBlob.type.includes('webm') ? '.webm' : '.m4a';
    formData.append('file', audioBlob, `voice_message${ext}`);
    
    const uploadRes = await axios.post(`${API_URL}/media/upload`, formData, {
      headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
    });
    
    setThreadMediaFile({
      file_url: uploadRes.data.file_url,
      file_name: uploadRes.data.file_name,
      file_type: 'audio',
      file_size: uploadRes.data.file_size,
      duration
    });
    setShowThreadVoiceRecorder(false);
  };

  const handlePostVoiceSend = async (audioBlob, duration) => {
    const formData = new FormData();
    const ext = audioBlob.type.includes('webm') ? '.webm' : '.m4a';
    formData.append('file', audioBlob, `voice_message${ext}`);
    
    const uploadRes = await axios.post(`${API_URL}/media/upload`, formData, {
      headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
    });
    
    // Immediately send as a post with audio
    try {
      const payload = {
        thread_id: selectedThread.id,
        content: 'Voice message',
        message_type: 'audio',
        file_url: uploadRes.data.file_url,
        file_name: uploadRes.data.file_name,
        file_size: uploadRes.data.file_size,
        duration
      };
      const response = await axios.post(
        `${API_URL}/community/posts`,
        payload,
        { headers: getAuthHeader() }
      );
      setPosts(prev => [...prev, response.data]);
      setThreads(threads.map(t => 
        t.id === selectedThread.id 
          ? { ...t, post_count: t.post_count + 1 }
          : t
      ));
      toast.success('Voice reply posted!');
    } catch (error) {
      toast.error('Failed to post voice reply');
    }
    setShowPostVoiceRecorder(false);
  };

  const formatTime = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return 'Recently';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Thread List View
  if (!selectedThread) {
    return (
      <div className="space-y-6" data-testid={`community-page-${communityType}`}>
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold text-stone-900">
                {title}
              </h1>
              {badgeText && (
                <span className={`text-white text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1 ${badgeColor}`}>
                  <Crown className="w-3 h-3" /> {badgeText}
                </span>
              )}
            </div>
            <p className="text-stone-600">Connect, share, and grow together</p>
          </div>

          <Dialog open={newThreadOpen} onOpenChange={setNewThreadOpen}>
            <DialogTrigger asChild>
              <Button className="btn-cosmic" data-testid="new-thread-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Thread
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-syne">Start a New Discussion</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Input
                    placeholder="Thread title..."
                    value={newThreadTitle}
                    onChange={(e) => setNewThreadTitle(e.target.value)}
                    className="input-cosmic"
                    data-testid="thread-title-input"
                  />
                </div>
                <div>
                  <MentionTextarea
                    ref={threadTextareaRef}
                    placeholder="What's on your mind?"
                    value={newThreadContent}
                    onChange={setNewThreadContent}
                    communityType={communityType}
                    className="input-cosmic min-h-[120px]"
                    rows={5}
                  />
                  <div className="flex justify-end gap-1 mt-2">
                    <FileAttachment
                      onFileUploaded={(data) => setThreadMediaFile(data)}
                      getAuthHeader={getAuthHeader}
                      disabled={submitting}
                    />
                    <button
                      type="button"
                      onClick={() => setShowThreadVoiceRecorder(!showThreadVoiceRecorder)}
                      className="p-2 hover:bg-stone-100 rounded-full transition-colors text-stone-400 hover:text-stone-600"
                      title="Record voice"
                    >
                      <Mic className="w-5 h-5" />
                    </button>
                    <div ref={threadGifRef} className="relative">
                      <button
                        type="button"
                        onClick={() => {
                          setShowThreadGifPicker(!showThreadGifPicker);
                          setShowThreadEmojiPicker(false);
                        }}
                        className="p-2 hover:bg-stone-100 rounded-full transition-colors text-stone-400 hover:text-stone-600"
                        title="Add GIF"
                      >
                        <Image className="w-5 h-5" />
                      </button>
                      {showThreadGifPicker && (
                        <div className="absolute bottom-10 right-0 z-50">
                          <GifPicker
                            onGifSelect={onThreadGifSelect}
                            onClose={() => setShowThreadGifPicker(false)}
                          />
                        </div>
                      )}
                    </div>
                    <div ref={threadEmojiRef} className="relative">
                      <button
                        type="button"
                        onClick={() => {
                          setShowThreadEmojiPicker(!showThreadEmojiPicker);
                          setShowThreadGifPicker(false);
                        }}
                        className="p-2 hover:bg-stone-100 rounded-full transition-colors text-stone-400 hover:text-stone-600"
                        title="Add emoji"
                      >
                        <Smile className="w-5 h-5" />
                      </button>
                      {showThreadEmojiPicker && (
                        <div className="absolute bottom-10 right-0 z-50">
                          <EmojiPicker
                            onEmojiClick={onThreadEmojiClick}
                            width={320}
                            height={350}
                            previewConfig={{ showPreview: false }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {showThreadVoiceRecorder && (
                  <div className="flex items-center justify-center py-2">
                    <VoiceRecorder
                      onSend={handleThreadVoiceSend}
                      onCancel={() => setShowThreadVoiceRecorder(false)}
                    />
                  </div>
                )}
                {threadMediaFile && (
                  <div className="flex items-center gap-2 bg-purple-50 rounded-lg px-3 py-2">
                    <span className="text-sm text-purple-700 truncate flex-1">{threadMediaFile.file_name}</span>
                    <button
                      type="button"
                      onClick={() => setThreadMediaFile(null)}
                      className="p-0.5 hover:bg-purple-100 rounded-full text-purple-400"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
                <Button 
                  onClick={handleCreateThread}
                  className="w-full btn-cosmic"
                  disabled={submitting}
                  data-testid="submit-thread-btn"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : null}
                  Create Thread
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Admin Bulk Actions */}
        {isAdmin && threads.length > 0 && (
          <div className="flex items-center justify-between bg-stone-50 p-3 rounded-lg border border-stone-200">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  handleSelectAll();
                }}
                className="flex items-center gap-2 text-sm text-stone-600 hover:text-stone-900 transition-colors"
                data-testid="select-all-threads"
              >
                {selectedThreadIds.length === threads.length ? (
                  <CheckSquare className="w-5 h-5 text-primary" />
                ) : (
                  <Square className="w-5 h-5" />
                )}
                {selectedThreadIds.length === threads.length ? 'Deselect All' : 'Select All'}
              </button>
              {selectedThreadIds.length > 0 && (
                <span className="text-sm text-stone-500">
                  {selectedThreadIds.length} selected
                </span>
              )}
            </div>
            {selectedThreadIds.length > 0 && (
              <Button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  handleBulkDelete();
                }}
                disabled={bulkDeleting}
                variant="destructive"
                size="sm"
                className="bg-red-500 hover:bg-red-600"
                data-testid="bulk-delete-threads"
              >
                {bulkDeleting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" />
                )}
                Delete Selected
              </Button>
            )}
          </div>
        )}

        {/* Threads List */}
        <div className="space-y-4">
          {threads.map((thread) => (
            <div
              key={thread.id}
              className={`w-full card-cosmic p-6 text-left card-hover ${thread.is_pinned ? 'border-2 border-purple-300 bg-purple-50/50' : ''} ${selectedThreadIds.includes(thread.id) ? 'ring-2 ring-primary' : ''}`}
              data-testid={`thread-item-${thread.id}`}
            >
              <div className="flex items-start gap-3">
                {/* Admin Checkbox */}
                {isAdmin && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleToggleSelect(thread.id, e);
                    }}
                    className="mt-1 text-stone-400 hover:text-primary transition-colors flex-shrink-0"
                    data-testid={`select-thread-${thread.id}`}
                  >
                    {selectedThreadIds.includes(thread.id) ? (
                      <CheckSquare className="w-5 h-5 text-primary" />
                    ) : (
                      <Square className="w-5 h-5" />
                    )}
                  </button>
                )}
                <button
                  onClick={() => handleSelectThread(thread)}
                  className="flex-1 text-left"
                >
                  <div className="flex items-center gap-2 mb-2">
                    {thread.is_pinned && (
                      <span className="text-purple-600 text-xs font-semibold px-2 py-0.5 rounded-full bg-purple-100">
                        📌 Pinned
                      </span>
                    )}
                    <h3 className="text-lg font-semibold text-stone-900">{thread.title}</h3>
                  </div>
                  {/* Thread content preview - show media, GIF thumbnail or text */}
                  {thread.file_url && thread.message_type && thread.message_type !== 'text' ? (
                    <div className="mb-3 flex items-center gap-2 text-stone-500 text-sm">
                      {thread.message_type === 'audio' && <><Mic className="w-4 h-4" /> Voice message</>}
                      {thread.message_type === 'video' && <><Film className="w-4 h-4" /> Video</>}
                      {thread.message_type === 'image' && (
                        <img src={`${process.env.REACT_APP_BACKEND_URL}${thread.file_url}`} alt="" className="rounded-lg" style={{ maxHeight: '80px', maxWidth: '120px' }} />
                      )}
                      {(thread.message_type === 'file' || thread.message_type === 'document') && <><FileText className="w-4 h-4" /> {thread.file_name}</>}
                      {thread.content && thread.content !== thread.file_name && (
                        <span className="text-stone-600">{thread.content.substring(0, 80)}</span>
                      )}
                    </div>
                  ) : thread.content.includes('[GIF]') ? (
                    <div className="mb-3">
                      {(() => {
                        const gifMatch = thread.content.match(/\[GIF\]\((.*?)\)/);
                        const textContent = thread.content.replace(/\[GIF\]\([^)]+\)/g, '').trim();
                        return (
                          <>
                            {textContent && <p className="text-stone-600 mb-2">{textContent}</p>}
                            {gifMatch && (
                              <img 
                                src={gifMatch[1]} 
                                alt="GIF" 
                                className="rounded-lg"
                                style={{ maxHeight: '100px', maxWidth: '150px' }}
                              />
                            )}
                          </>
                        );
                      })()}
                    </div>
                  ) : (
                    <p className="text-stone-600 line-clamp-2 mb-3">{thread.content}</p>
                  )}
                  <div className="flex items-center gap-4 text-sm text-stone-500">
                    <span className="flex items-center gap-1">
                      <User className="w-4 h-4" />
                      {thread.author_name}
                    </span>
                    <span className="flex items-center gap-1">
                      <MessageCircle className="w-4 h-4" />
                      {thread.post_count} replies
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {formatTime(thread.created_at)}
                    </span>
                  </div>
                </button>
                {/* Admin Delete Button */}
                {isAdmin && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleDeleteThread(thread.id, e);
                    }}
                    disabled={deleting}
                    className="p-2 text-stone-400 hover:text-red-500 transition-colors flex-shrink-0"
                    title="Delete thread"
                    data-testid={`delete-thread-${thread.id}`}
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {threads.length === 0 && (
          <div className="card-cosmic p-12 text-center">
            <Users className="w-12 h-12 text-stone-400 mx-auto mb-4" />
            <p className="text-stone-600 mb-4">No discussions yet. Be the first to start one!</p>
            <Button 
              onClick={() => setNewThreadOpen(true)}
              className="btn-cosmic"
            >
              Start a Discussion
            </Button>
          </div>
        )}
      </div>
    );
  }

  // Thread Detail View
  return (
    <div className="space-y-6" data-testid="thread-detail-view">
      <button
        onClick={() => setSelectedThread(null)}
        className="flex items-center gap-2 text-stone-600 hover:text-primary transition-colors"
        data-testid="back-to-threads-btn"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to discussions
      </button>

      {/* Thread */}
      <div className="card-cosmic p-6">
        <h1 className="text-2xl font-bold text-stone-900 mb-4">{selectedThread.title}</h1>
        <div className="text-stone-700 whitespace-pre-wrap mb-4">
          {selectedThread.file_url && selectedThread.message_type && selectedThread.message_type !== 'text' && (
            <div className="mb-3">
              <MediaRenderer
                messageType={selectedThread.message_type}
                fileUrl={selectedThread.file_url}
                fileName={selectedThread.file_name}
                fileSize={selectedThread.file_size}
                duration={selectedThread.duration}
                isOwn={false}
              />
            </div>
          )}
          {selectedThread.content.split(/(\[GIF\]\([^)]+\))/g).map((part, index) => {
            const gifMatch = part.match(/\[GIF\]\((.*?)\)/);
            if (gifMatch) {
              return (
                <img 
                  key={index}
                  src={gifMatch[1]} 
                  alt="GIF" 
                  className="rounded-lg max-w-full my-2"
                  style={{ maxHeight: '250px' }}
                />
              );
            }
            return part ? <span key={index}>{part}</span> : null;
          })}
        </div>
        <div className="flex items-center gap-4 text-sm text-stone-500 pt-4 border-t border-stone-200">
          <span className="flex items-center gap-1">
            <User className="w-4 h-4" />
            {selectedThread.author_name}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            {formatTime(selectedThread.created_at)}
          </span>
        </div>
        
        {/* Thread Reactions */}
        <div className="flex items-center flex-wrap gap-2 mt-4 pt-4 border-t border-stone-200">
          {selectedThread.reactions && Object.entries(selectedThread.reactions).map(([emoji, users]) => (
            <button
              key={emoji}
              onClick={() => handleAddReaction(selectedThread.id, emoji, true)}
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm transition-colors ${
                users.includes(user?.id)
                  ? 'bg-purple-100 border-2 border-purple-300'
                  : 'bg-stone-100 hover:bg-stone-200 border-2 border-transparent'
              }`}
              title={`${users.length} reaction${users.length > 1 ? 's' : ''}`}
            >
              <span>{emoji}</span>
              <span className="text-xs text-stone-600">{users.length}</span>
            </button>
          ))}
          
          {/* Add Reaction Button for Thread */}
          <div className="relative" ref={showReactionPicker === 'thread' ? reactionPickerRef : null}>
            <button
              onClick={() => setShowReactionPicker(showReactionPicker === 'thread' ? null : 'thread')}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm bg-stone-100 hover:bg-stone-200 transition-colors text-stone-500"
              title="Add reaction"
            >
              <SmilePlus className="w-4 h-4" />
            </button>
            
            {showReactionPicker === 'thread' && (
              <div className="absolute bottom-10 left-0 z-50 bg-white rounded-lg shadow-lg border border-stone-200 p-2">
                <div className="flex gap-1 mb-2">
                  {quickReactions.map(emoji => (
                    <button
                      key={emoji}
                      onClick={() => handleAddReaction(selectedThread.id, emoji, true)}
                      className="p-2 hover:bg-stone-100 rounded-lg text-xl transition-colors"
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setShowReactionPicker('picker-thread')}
                  className="w-full text-xs text-stone-500 hover:text-stone-700 py-1"
                >
                  More emojis...
                </button>
              </div>
            )}
            
            {showReactionPicker === 'picker-thread' && (
              <div className="absolute bottom-10 left-0 z-50">
                <EmojiPicker
                  onEmojiClick={(emojiData) => handleAddReaction(selectedThread.id, emojiData.emoji, true)}
                  width={320}
                  height={350}
                  previewConfig={{ showPreview: false }}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Replies */}
      <div>
        <h2 className="text-lg font-semibold text-stone-900 mb-4">
          Replies ({posts.length})
        </h2>
        
        <ScrollArea className="h-[400px] pr-4">
          <div className="space-y-4">
            {posts.map((post) => {
              // Parse content for GIFs
              const renderContent = (content) => {
                const parts = content.split(/(\[GIF\]\([^)]+\))/g);
                return parts.map((part, index) => {
                  const gifMatch = part.match(/\[GIF\]\((.*?)\)/);
                  if (gifMatch) {
                    return (
                      <img 
                        key={index}
                        src={gifMatch[1]} 
                        alt="GIF" 
                        className="rounded-lg max-w-full my-2"
                        style={{ maxHeight: '200px' }}
                      />
                    );
                  }
                  return part ? <span key={index}>{part}</span> : null;
                });
              };

              return (
                <div key={post.id} className="card-cosmic p-5" data-testid={`post-item-${post.id}`}>
                  {/* Media content */}
                  {post.file_url && post.message_type && post.message_type !== 'text' && (
                    <div className="mb-3">
                      <MediaRenderer
                        messageType={post.message_type}
                        fileUrl={post.file_url}
                        fileName={post.file_name}
                        fileSize={post.file_size}
                        duration={post.duration}
                        isOwn={false}
                      />
                    </div>
                  )}
                  <div className="text-stone-700 whitespace-pre-wrap mb-3">{renderContent(post.content)}</div>
                  
                  {/* Reactions Display */}
                  <div className="flex items-center flex-wrap gap-2 mb-3">
                    {post.reactions && Object.entries(post.reactions).map(([emoji, users]) => (
                      <button
                        key={emoji}
                        onClick={() => handleAddReaction(post.id, emoji)}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm transition-colors ${
                          users.includes(user?.id)
                            ? 'bg-purple-100 border-2 border-purple-300'
                            : 'bg-stone-100 hover:bg-stone-200 border-2 border-transparent'
                        }`}
                        title={`${users.length} reaction${users.length > 1 ? 's' : ''}`}
                      >
                        <span>{emoji}</span>
                        <span className="text-xs text-stone-600">{users.length}</span>
                      </button>
                    ))}
                    
                    {/* Add Reaction Button */}
                    <div className="relative" ref={showReactionPicker === post.id ? reactionPickerRef : null}>
                      <button
                        onClick={() => setShowReactionPicker(showReactionPicker === post.id ? null : post.id)}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm bg-stone-100 hover:bg-stone-200 transition-colors text-stone-500"
                        title="Add reaction"
                      >
                        <SmilePlus className="w-4 h-4" />
                      </button>
                      
                      {showReactionPicker === post.id && (
                        <div className="absolute bottom-10 left-0 z-50 bg-white rounded-lg shadow-lg border border-stone-200 p-2">
                          <div className="flex gap-1 mb-2">
                            {quickReactions.map(emoji => (
                              <button
                                key={emoji}
                                onClick={() => handleAddReaction(post.id, emoji)}
                                className="p-2 hover:bg-stone-100 rounded-lg text-xl transition-colors"
                              >
                                {emoji}
                              </button>
                            ))}
                          </div>
                          <button
                            onClick={() => {
                              setShowReactionPicker(`picker-${post.id}`);
                            }}
                            className="w-full text-xs text-stone-500 hover:text-stone-700 py-1"
                          >
                            More emojis...
                          </button>
                        </div>
                      )}
                      
                      {showReactionPicker === `picker-${post.id}` && (
                        <div className="absolute bottom-10 left-0 z-50">
                          <EmojiPicker
                            onEmojiClick={(emojiData) => handleAddReaction(post.id, emojiData.emoji)}
                            width={320}
                            height={350}
                            previewConfig={{ showPreview: false }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm text-stone-500">
                    <span className="flex items-center gap-1">
                      <User className="w-4 h-4" />
                      {post.author_name}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {formatTime(post.created_at)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </ScrollArea>

        {posts.length === 0 && (
          <div className="card-cosmic p-8 text-center">
            <MessageCircle className="w-8 h-8 text-stone-400 mx-auto mb-2" />
            <p className="text-stone-500">No replies yet. Be the first to respond!</p>
          </div>
        )}
      </div>

      {/* Reply Input */}
      <div className="card-cosmic p-4">
        {showPostVoiceRecorder ? (
          <div className="flex items-center justify-center py-2">
            <VoiceRecorder
              onSend={handlePostVoiceSend}
              onCancel={() => setShowPostVoiceRecorder(false)}
            />
          </div>
        ) : (
          <div className="flex gap-4">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-secondary to-primary flex items-center justify-center text-white font-semibold flex-shrink-0">
              {user?.name?.charAt(0) || 'U'}
            </div>
            <div className="flex-1">
              {postMediaFile && (
                <div className="flex items-center gap-2 bg-purple-50 rounded-lg px-3 py-2 mb-2">
                  <span className="text-sm text-purple-700 truncate flex-1">{postMediaFile.file_name}</span>
                  <button
                    type="button"
                    onClick={() => setPostMediaFile(null)}
                    className="p-0.5 hover:bg-purple-100 rounded-full text-purple-400"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
              <MentionTextarea
                ref={postTextareaRef}
                placeholder="Write a reply..."
                value={newPostContent}
                onChange={setNewPostContent}
                communityType={communityType}
                className="input-cosmic min-h-[80px]"
                rows={3}
              />
              <div className="flex justify-end gap-1 mt-2 mb-3">
                <FileAttachment
                  onFileUploaded={(data) => setPostMediaFile(data)}
                  getAuthHeader={getAuthHeader}
                  disabled={submitting}
                />
                <button
                  type="button"
                  onClick={() => setShowPostVoiceRecorder(true)}
                  className="p-2 hover:bg-stone-100 rounded-full transition-colors text-stone-400 hover:text-stone-600"
                  title="Record voice"
                  data-testid="community-voice-btn"
                >
                  <Mic className="w-5 h-5" />
                </button>
                <div ref={postGifRef} className="relative">
                  <button
                    type="button"
                    onClick={() => {
                      setShowPostGifPicker(!showPostGifPicker);
                      setShowPostEmojiPicker(false);
                    }}
                    className="p-2 hover:bg-stone-100 rounded-full transition-colors text-stone-400 hover:text-stone-600"
                    title="Add GIF"
                  >
                    <Image className="w-5 h-5" />
                  </button>
                  {showPostGifPicker && (
                    <div className="absolute bottom-10 right-0 z-50">
                      <GifPicker
                        onGifSelect={onPostGifSelect}
                        onClose={() => setShowPostGifPicker(false)}
                      />
                    </div>
                  )}
                </div>
                <div ref={postEmojiRef} className="relative">
                  <button
                    type="button"
                    onClick={() => {
                      setShowPostEmojiPicker(!showPostEmojiPicker);
                      setShowPostGifPicker(false);
                    }}
                    className="p-2 hover:bg-stone-100 rounded-full transition-colors text-stone-400 hover:text-stone-600"
                    title="Add emoji"
                  >
                    <Smile className="w-5 h-5" />
                  </button>
                  {showPostEmojiPicker && (
                    <div className="absolute bottom-10 right-0 z-50">
                      <EmojiPicker
                        onEmojiClick={onPostEmojiClick}
                        width={320}
                        height={350}
                        previewConfig={{ showPreview: false }}
                      />
                    </div>
                  )}
                </div>
              </div>
              <div className="flex justify-end">
                <Button
                  onClick={handleCreatePost}
                  className="btn-cosmic"
                  disabled={submitting || (!newPostContent.trim() && !postMediaFile)}
                  data-testid="submit-reply-btn"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Post Reply
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Export community components
export const FreeCommunityPage = () => (
  <CommunityPage 
    communityType="free" 
    title="Community" 
  />
);

export const FoundingCommunityPage = () => (
  <CommunityPage 
    communityType="founding" 
    title="Founding Community Conversation" 
    badgeText="Founding"
    badgeColor="bg-gradient-to-r from-purple-500 to-pink-500"
  />
);

export const VipCommunityPage = () => (
  <CommunityPage 
    communityType="vip" 
    title="VIP Member Conversations" 
    badgeText="VIP"
    badgeColor="bg-gradient-to-r from-amber-500 to-orange-500"
  />
);
